/* eslint-disable react-hooks/exhaustive-deps */

import { useRef, useState, useEffect } from 'react';
import { animated } from 'react-spring';

import { Tag } from '@blueprint/ui';
import {
    ChevronLeftSmall,
    ChevronRightSmall,
    ImageSmall
} from '@zoopla-eng/zds-components/icons';
import { ImagePlaceholder } from '@zoopla/assets';
import {
    Wrapper,
    Container,
    Viewport,
    SlideContainer,
    Placeholder,
    ProgressIndicator,
    ButtonPrev,
    ButtonNext,
    SlideControlsContainer
} from './Gallery.styles';
import { usePrefersReducedMotion } from '@zoopla/dom-util';
import { useGallery, usePreventImageDrag, useOnArrowKeyDown } from './hooks';
import { GalleryGestureLockProvider } from './context/GalleryGestureLock';
import { GalleryImage, GalleryProps } from './Gallery.types';
import GallerySlide from './GallerySlide';

// Allow react-spring to update the Slides container styling outside
// of the react render lifecycle (performance)
const AnimatedSlideContainer = animated(SlideContainer);

const Gallery = ({
    images = [],
    initialSlide = 0,
    updateSlideIndex = initialSlide,
    allowCropping = false,
    icon,
    hasClickDragging = true,
    hasKeyboardControls = true,
    hasArrowsOnImage = false,
    hideProgressIndicator = false,
    hasZoomControls = false,
    createCaption = (index: number, total: number) =>
        `Image ${index + 1} of ${total}`,
    onSlideClick,
    onSlidePrevious,
    onSlideNext,
    onSlideChanged,
    onSlideZoom,
    slideProgressIndicatorCTA
}: GalleryProps) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const controlsPortalRef = useRef<HTMLDivElement>(null);
    const isReducedMotion = usePrefersReducedMotion();

    const {
        viewportOffset,
        touchEventHandlers,
        controls,
        viewportWidth,
        viewportHeight,
        slideIndex
    } = useGallery(
        images,
        initialSlide,
        viewportRef,
        !isReducedMotion,
        onSlideNext,
        onSlidePrevious,
        onSlideChanged
    );

    // We don't want to load anything other than the first image before the
    // user interacts with the gallery (to reduce unnecessary data fetching)
    const [isUserInteractingWithGallery, setisUserInteractingWithGallery] =
        useState(false);

    // Prevent default browser behaviour of dragging the image out of the
    // document (this breaks the swipe/drag functionality)
    usePreventImageDrag(viewportRef);

    const numSlides = images.length;
    const hasSlides = numSlides > 0;
    const hasMultipleSlides = numSlides > 1;
    const isKeyboardEnabled = hasKeyboardControls && hasMultipleSlides;

    useOnArrowKeyDown(
        isKeyboardEnabled ? controls.previous : () => null,
        isKeyboardEnabled ? controls.next : () => null
    );

    useEffect(() => {
        if (slideIndex !== updateSlideIndex) {
            controls.set(updateSlideIndex);
            setisUserInteractingWithGallery(true);
        }
    }, [updateSlideIndex]);

    const renderSlide = (item: GalleryImage, index: number) => (
        <GallerySlide
            key={index}
            item={item}
            isActive={index === slideIndex}
            isFirst={index === 0}
            caption={createCaption(index, numSlides)}
            viewportRef={viewportRef}
            viewportWidth={viewportWidth}
            viewportHeight={viewportHeight}
            controlsPortalRef={controlsPortalRef}
            allowCropping={allowCropping}
            allowImagePrefetching={isUserInteractingWithGallery}
            allowMotion={!isReducedMotion}
            onSlideClick={() => onSlideClick && onSlideClick(index)}
            onSlideZoom={() => onSlideZoom && onSlideZoom(index)}
        />
    );

    const renderSlideClone = (
        item: GalleryImage,
        position: 'first' | 'last'
    ) => (
        <GallerySlide
            key={position}
            isClone={true}
            item={item}
            caption=""
            viewportRef={viewportRef}
            viewportWidth={viewportWidth}
            viewportHeight={viewportHeight}
            controlsPortalRef={controlsPortalRef}
            allowCropping={allowCropping}
            allowImagePrefetching={isUserInteractingWithGallery}
            allowMotion={!isReducedMotion}
        />
    );

    // We only care about this the first time it changes so don't endlessly listen
    const isUserInteractingWithGalleryHandler = isUserInteractingWithGallery
        ? undefined
        : () => setisUserInteractingWithGallery(true);

    const bindTouchEventHandlers = hasClickDragging ? touchEventHandlers() : {};

    return (
        <Wrapper
            onMouseMove={isUserInteractingWithGalleryHandler}
            onTouchStart={isUserInteractingWithGalleryHandler}
        >
            <Container
                data-testid="gallery"
                hasArrowsOnImage={hasArrowsOnImage}
            >
                <Viewport ref={viewportRef}>
                    {hasSlides && (
                        <AnimatedSlideContainer
                            style={{
                                transform: viewportOffset.interpolate(
                                    (x) => `translateX(${x}px)`
                                )
                            }}
                            hasMultipleSlides={hasMultipleSlides}
                            aria-live="polite"
                            {...bindTouchEventHandlers}
                        >
                            {/* Render a duplicate of the last slide as the first item */}
                            {hasMultipleSlides &&
                                renderSlideClone(
                                    images[images.length - 1],
                                    'first'
                                )}

                            {/* Render all real slides in order */}
                            {images.map(renderSlide)}

                            {/* Render a duplicate of the first slide as the last item */}
                            {hasMultipleSlides &&
                                renderSlideClone(images[0], 'last')}
                        </AnimatedSlideContainer>
                    )}
                    {!hasSlides && (
                        <Placeholder>
                            <img
                                src={ImagePlaceholder}
                                aria-label="No images to display"
                                loading="lazy"
                            />
                        </Placeholder>
                    )}
                    {!hideProgressIndicator && (
                        <ProgressIndicator
                            data-testid="gallery-counter"
                            aria-hidden
                        >
                            <Tag>
                                {icon || (
                                    <ImageSmall fill="$graphicBackgroundDefault" />
                                )}
                                {numSlides
                                    ? `${slideIndex + 1}/${numSlides}`
                                    : '0'}
                                {slideProgressIndicatorCTA}
                            </Tag>
                        </ProgressIndicator>
                    )}
                </Viewport>
            </Container>
            {numSlides > 1 && (
                <>
                    <ButtonPrev
                        variant="secondary"
                        hasArrowsOnImage={hasArrowsOnImage}
                        onClick={() => controls.previous()}
                        aria-label="Previous image"
                        data-testid="arrow_left"
                    >
                        <ChevronLeftSmall />
                    </ButtonPrev>

                    <ButtonNext
                        variant="secondary"
                        hasArrowsOnImage={hasArrowsOnImage}
                        onClick={() => controls.next()}
                        aria-label="Next image"
                        data-testid="arrow_right"
                    >
                        <ChevronRightSmall />
                    </ButtonNext>
                </>
            )}

            <SlideControlsContainer
                ref={controlsPortalRef}
                isVisible={hasZoomControls}
            />
        </Wrapper>
    );
};

// The gesture lock context needs to be available at the top-level gallery
// component level
const GalleryWrapper = (props: GalleryProps) => {
    return (
        <GalleryGestureLockProvider>
            <Gallery {...props} />
        </GalleryGestureLockProvider>
    );
};

export default GalleryWrapper;
